import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ImageLightbox.css';

export default function ImageLightbox({ images, index, onChange, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const handlers = useRef({ onClose, onChange });
  handlers.current = { onClose, onChange };
  const open = index !== null && Boolean(images[index]);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const { overflow, paddingRight } = document.body.style;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalPadding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${originalPadding + scrollbarWidth}px`;
    closeRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); handlers.current.onClose(); }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        handlers.current.onChange(event.key === 'ArrowLeft' ? -1 : 1);
      }
      if (event.key === 'Tab') {
        const buttons = dialogRef.current?.querySelectorAll('button');
        const first = buttons?.[0];
        const last = buttons?.[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      window.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  const item = images[index];
  const src = typeof item === 'string' ? item : item.src;
  const alt = typeof item === 'string' ? `Gallery image ${index + 1}` : item.alt || item.filename || `Gallery image ${index + 1}`;
  return createPortal(
    <div className="image-lightbox" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Image preview">
      <div className="image-lightbox__backdrop" onClick={onClose} aria-hidden="true" />
      <button ref={closeRef} type="button" className="image-lightbox__close" aria-label="Close image preview" onClick={onClose}><span aria-hidden="true">×</span></button>
      <div className="image-lightbox__stage">
        <img src={`${process.env.PUBLIC_URL}${src}`} alt={alt} className="image-lightbox__image" />
      </div>
      <div className="image-lightbox__controls">
        <button type="button" aria-label="Previous image" onClick={() => onChange(-1)}>←</button>
        <span aria-live="polite">{index + 1} / {images.length}</span>
        <button type="button" aria-label="Next image" onClick={() => onChange(1)}>→</button>
      </div>
    </div>, document.body,
  );
}
